import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useContext, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { CheckBoxReferralIcon } from '../../../assets';
import { RefetchLateral } from '../../../context/refetchLateral/refetchProvider';
import { UPDATE_CAMPAIGN } from '../../../graphql/campaign/mutation';
import { GET_SETTINGS } from '../../../graphql/campaign/query';
import { useNotification } from '../../../hooks/useNotification';
import { usePrompt } from '../../../hooks/usePrompt';
import { Button } from '../../common/Button';
import { BrandGroupDropdown } from '../../inputs/BrandGroupDropdown';
import { DatePickerInput } from '../../inputs/DatePicker';
import { GenericDropdown } from '../../inputs/GenericDropdown';
import { MoneyInput } from '../../inputs/MoneyInput';
import { TextInput } from '../../inputs/TextInput';
import { Toggle } from '../../inputs/Toggle';
import { UserDropdown } from '../../inputs/UserDropdown';
import { UserDropdownList } from '../../inputs/UserDropdownList';
import { MigratePiecesPopup } from '../../popups/MigratePiecesPopup';
import DescriptionPlatforms from '../settings/Description';
import DisclaimerPlatforms from '../settings/Disclaimer';
// import SelectInfluencer from '../settings/SelectInfluencer';

// import customParseFormat from 'dayjs/plugin/customParseFormat' // ES 2015

dayjs.extend(customParseFormat);

const CUSTOM_BUTTON_CLASSNAMES = '!text-influentia-light';

type Settings = {
  campaignSettings: {
    data: {
      id: string;
      month?: string | Date;
      name: string;
      money: number;
      currency: string;
      brand: {
        id: string;
        name: string;
      };
      briefing: {
        id: string;
        name: string;
        url: string;
      };
      accountManagers: {
        id: string;
        name: string;
        profile_picture: string;
      }[];
      influencers: {
        id: string;
        name: string;
        profile_picture: string;
        has_variable: boolean;
      }[];
      disclaimers: {
        id: string;
        platform: string;
        disclaimer: string;
      }[];
      descriptions: {
        id: string;
        description: string;
        platform: string;
      }[];
      group_id: string;
      // IPDC-376: agency fee billing model
      agency_fee_enabled?: boolean;
      agency_fee_mode?: 'global' | 'per_influencer' | null;
      agency_fee_global_percentage?: number | null;
      agency_fee_calculation_base?: 'influencer' | 'brand' | null;
      weighted_fee_percentage?: number | null;
    };
  };
};

export const SingleCampaignSettingsTab = () => {
  const [campaignId]: [string] = useOutletContext();
  const { t } = useTranslation('common');
  const { refetchLateral, refetchTabs } = useContext(RefetchLateral);
  const [hasVariablePricing, setHasVariablePricing] = useState(false);
  const [showMigratePopup, setShowMigratePopup] = useState(false);

  const methods = useForm<Settings['campaignSettings']['data']>();
  const {
    control,
    setValue,
    watch,
    reset,
    handleSubmit,
    formState: { dirtyFields },
  } = methods;

  const {
    fields: descriptions,
    append: appendDescription,
    remove: removeDescription,
    update: updateDescription,
  } = useFieldArray({
    name: 'descriptions',
    control,
  });

  const {
    fields: disclaimers,
    append: appendDisclaimer,
    remove: removeDisclaimer,
    update: updateDisclaimer,
  } = useFieldArray({
    name: 'disclaimers',
    control,
  });

  const [dirty, setDirty] = useState(false);
  const { refetch } = useQuery<Settings>(GET_SETTINGS, {
    variables: {
      campaignId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const info = data.campaignSettings.data;
      setHasVariablePricing(info.influencers.some((i) => i.has_variable));
      reset({
        ...info,
        month: info.month
          ? dayjs(info.month, 'MM/YYYY').format('MM/DD/YYYY')
          : '',
        influencers: info.influencers.map((i) => ({
          value: i.id,
          id: i.id,
        })) as any,
        accountManagers: info.accountManagers?.map((i) => ({
          value: i.id,
          id: i.id,
        })) as any,
      });
    },
  });
  const addDescription = () => {
    appendDescription({
      id: Math.floor(Math.random() * 1000).toString(),
      description: '',
      platform: '',
    });
  };

  const addDisclaimer = () => {
    appendDisclaimer({
      id: Math.floor(Math.random() * 1000).toString(),
      disclaimer: '',
      platform: '',
    });
  };

  const deleteDescription = (index: number) => {
    removeDescription(index);
  };

  const deleteDisclaimer = (index: number) => {
    removeDisclaimer(index);
  };

  const { notifySaveSuccess, notifySaveError } = useNotification();
  const [updateCampaign, { loading }] = useMutation(UPDATE_CAMPAIGN);

  const onCompleted = () => {
    notifySaveSuccess();
    refetch();
    setDirty(false);
    reset({}, { keepValues: true });
    refetchLateral();
    refetchTabs();
  };

  const handleSave = (data: Settings['campaignSettings']['data']) => {
    const headers: any = {
      'x-apollo-operation-name': 'setCampaignSettings',
    };
    // IPDC-376: when agency fee is enabled, the campaign-level `money` field
    // is locked (computed as SUM of child total_amount) and must NOT be
    // sent to the server as a free edit. Send null in that case so the
    // server-side SUM-sync (driven by the afterUpdate hook) is authoritative.
    const feeEnabled = !!data.agency_fee_enabled;
    const moneyToSend = feeEnabled ? null : Number(data.money);
    const agencyFeeMode = feeEnabled ? data.agency_fee_mode ?? null : null;
    const isGlobal = feeEnabled && data.agency_fee_mode === 'global';
    const agencyFeeGlobalPercentage =
      isGlobal && data.agency_fee_global_percentage != null
        ? Number(data.agency_fee_global_percentage)
        : null;
    // Bidirectional fee: only forward the direction when the toggle is
    // enabled — when fee is off the column default ('influencer')
    // persists naturally.
    const agencyFeeCalculationBase = feeEnabled
      ? data.agency_fee_calculation_base ?? 'brand'
      : null;

    updateCampaign({
      variables: {
        campaignId,
        name: data.name,
        month: data.month ? dayjs(data.month).format('MM/YYYY') : '',
        descriptions: data.descriptions.map((description) => {
          return {
            description: description.description,
            platform: description.platform,
          };
        }),
        disclaimers: data.disclaimers.map((disclaimer) => {
          return {
            disclaimer: disclaimer.disclaimer,
            platform: disclaimer.platform,
          };
        }),
        money: moneyToSend,
        currency: data.currency,
        brandId: data.brand.id,
        influencers: data.influencers
          .map((influencer: any) => influencer.value)
          .filter((id) => id),
        accountManagers: data.accountManagers
          .map((accountManager: any) => accountManager.value)
          .filter((id) => id),
        group_id: data.group_id,
        // IPDC-376: agency fee billing model
        agencyFeeEnabled: feeEnabled,
        agencyFeeMode,
        agencyFeeGlobalPercentage,
        // Bidirectional fee: campaign-level direction toggle.
        agencyFeeCalculationBase,
      },
      context: {
        headers,
      },
      onCompleted: () => {
        onCompleted();
      },
      onError: () => {
        notifySaveError();
      },
    });
  };

  usePrompt({
    isDirty: Object.keys(dirtyFields).length > 0 || dirty,
  });

  return (
    <>
      {showMigratePopup && (
        <MigratePiecesPopup
          campaignId={campaignId}
          setOpen={setShowMigratePopup}
          refetch={refetch}
        />
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="flex lg:flex-row flex-col gap-5">
            <TextInput
              type="text"
              label={t('singleCampaign.campaignName')}
              selectedValue={watch('name')}
              onSelectChange={(value) =>
                setValue('name', value as string, { shouldDirty: true })
              }
            />
            <div className="w-full">
              <UserDropdown
                control={control}
                name="brand.id"
                label={t('campaigns.createClient')}
                placeholder={t('campaigns.createClientPlaceholder')}
                type="brand"
                isClearable={true}
                searchable
              />
            </div>
          </div>
          <div className="flex lg:flex-row flex-col mt-5 gap-5 items-start">
            <div className="w-full">
              <BrandGroupDropdown
                brand_id={watch('brand.id')}
                label={t('singleCampaign.assignGroup')}
                name="group_id"
                control={control}
                isClearable={false}
                defaultToAllUsers
              />
            </div>
            <div className="flex lg:flex-row flex-col gap-5 items-start w-full">
              <div className="flex flex-row gap-x-2 items-center">
                <MoneyInput
                  confirmPopup={true}
                  label={t('singleCampaign.totalAmount')}
                  name="money"
                  currentCurrency={watch('currency')}
                  control={control}
                  currencySelect={true}
                  disabled={!!watch('agency_fee_enabled')}
                />
                {hasVariablePricing && (
                  <p className="flex-none mt-8 text-sm">
                    {t('singleCampaign.variables')}
                  </p>
                )}
              </div>
              <div className="flex items-center">
                <DatePickerInput
                  name="month"
                  monthYearPicker
                  isClearable
                  label={t('singleCampaign.campaignMonth')}
                  control={control}
                  fullSpace
                />
              </div>
            </div>
          </div>

          {/* IPDC-376: Agency Fee billing model — campaign-level toggle + mode + global %.
              Decoupled revision: in `per_influencer` mode the global %
              MoneyInput is HIDDEN entirely (was previously a disabled
              "último global" field); replaced with an informational
              redirect message that points the user to Ajustes Influencer. */}
          <div className="flex flex-col gap-3 mt-5 p-4 border border-influentia-light-blue/30 rounded-md">
            <div className="flex flex-row items-center gap-3">
              <Toggle
                label="Habilitar fee de agencia"
                value={!!watch('agency_fee_enabled')}
                onChange={(v: boolean) => {
                  const wasEnabled = !!watch('agency_fee_enabled');
                  setValue('agency_fee_enabled', v, { shouldDirty: true });
                  // IPDC-376: default the calculation base to "brand" (fee
                  // applied on the total price) whenever the fee goes from
                  // disabled to enabled — the base is irrelevant while
                  // disabled, so re-enabling should reset it to the
                  // preferred default instead of surfacing a stale value.
                  if (v && !wasEnabled) {
                    setValue('agency_fee_calculation_base', 'brand', {
                      shouldDirty: true,
                    });
                  }
                  // Disabling the fee clears the whole section (mode, global %
                  // and calculation base) so nothing stale is shown or sent.
                  if (!v) {
                    setValue('agency_fee_mode', null, { shouldDirty: true });
                    setValue('agency_fee_global_percentage', null, {
                      shouldDirty: true,
                    });
                    setValue('agency_fee_calculation_base', null, {
                      shouldDirty: true,
                    });
                  }
                }}
                maxWidth="max-w-[260px]"
              />
              {watch('weighted_fee_percentage') !== null &&
                watch('weighted_fee_percentage') !== undefined && (
                  <span className="text-sm text-gray-500">
                    Fee agencia total ponderado:{' '}
                    <strong>
                      {Number(watch('weighted_fee_percentage')).toFixed(2)}%
                    </strong>
                  </span>
                )}
            </div>

            {!!watch('agency_fee_enabled') && (
              <div className="flex lg:flex-row flex-col gap-5 items-start">
                <div className="w-full max-w-[260px]">
                  <GenericDropdown
                    name="agency_fee_mode"
                    label="Modo fee de agencia"
                    control={control}
                    isClearable={false}
                    options={[
                      { value: 'global', label: 'Global (un % para todos)' },
                      { value: 'per_influencer', label: 'Por influencer' },
                    ]}
                  />
                </div>
                {watch('agency_fee_mode') === 'global' && (
                  <div className="w-full max-w-[260px]">
                    <MoneyInput
                      label="Porcentaje de agencia"
                      name="agency_fee_global_percentage"
                      currentCurrency="%"
                      control={control}
                      currencySelect={false}
                    />
                  </div>
                )}
                {watch('agency_fee_mode') === 'per_influencer' && (
                  <div className="w-full max-w-[260px]">
                    <p className="my-2 block text-[14px] font-[600]">
                      Porcentaje de agencia
                    </p>
                    <p className="text-sm text-gray-500">
                      Configura el porcentaje de fee por influencer en Ajustes
                      Influencer.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Bidirectional fee: campaign-level direction toggle. Gated on
                `agency_fee_enabled` per the spec — irrelevant when fee is
                off. Default value comes from the server column. */}
            {!!watch('agency_fee_enabled') && (
              <div className="flex lg:flex-row flex-col gap-5 items-center mt-3">
                <div className="w-full max-w-[260px]">
                  <GenericDropdown
                    name="agency_fee_calculation_base"
                    label={t('singleCampaign.agencyFee.calculationBaseLabel')}
                    control={control}
                    isClearable={false}
                    options={[
                      {
                        value: 'influencer',
                        label: t(
                          'singleCampaign.agencyFee.calculationBaseInfluencer',
                        ),
                      },
                      {
                        value: 'brand',
                        label: t(
                          'singleCampaign.agencyFee.calculationBaseBrand',
                        ),
                      },
                    ]}
                  />
                </div>
                <p className="text-sm text-gray-500 max-w-[420px]">
                  {watch('agency_fee_calculation_base') === 'brand'
                    ? t('singleCampaign.agencyFee.disclaimerBrand')
                    : t('singleCampaign.agencyFee.disclaimerInfluencer')}
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1 mt-5">
            <UserDropdownList
              type="influencer"
              accessName="influencers"
              label={t('singleCampaign.addinfluencers')}
              accessKey="value"
              addText={t('campaigns.createInfluencersAdd')}
              placeholderSelects={t('campaigns.createInfluencersPlaceholder')}
              control={control}
              minLength={0}
              trashIconClassname="fill-current text-red-status hover:text-influentia-light-blue"
              buttonClassname="hover:text-influentia-light-blue"
            />
          </div>
          <div className="flex flex-col gap-1 mt-5">
            <UserDropdownList
              type="influentia"
              accessName="accountManagers"
              label={t('campaigns.createAccountManagers')}
              accessKey="value"
              addText={t('campaigns.createAccountManagersAdd')}
              placeholderSelects={t(
                'campaigns.createAccountManagersPlaceholder',
              )}
              control={control}
              minLength={0}
              trashIconClassname="fill-current text-red-status hover:text-influentia-light-blue"
              buttonClassname="hover:text-influentia-light-blue"
            />
          </div>
          <div className="flex flex-col gap-1 mt-5">
            <label className="my-2 block text-[14px] font-[600]">
              {t('singleCampaign.disclaimer')}
            </label>
            {disclaimers &&
              disclaimers.map((disclaimer, index) => (
                <DisclaimerPlatforms
                  key={disclaimer.id}
                  disclaimer={disclaimer}
                  updateDisclaimer={(d) => updateDisclaimer(index, d)}
                  deleteDisclaimer={() => deleteDisclaimer(index)}
                />
              ))}
            <div className="w-full flex items-center justify-center">
              <Button
                style="light-blue-underline"
                text={t('singleCampaign.addDisclaimer')}
                type="button"
                customClassnames="hover:text-influentia-light-blue"
                id="addInfluencer"
                iconLeft={<CheckBoxReferralIcon />}
                onClick={() => addDisclaimer()}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 mt-5">
            <label className="my-2 block text-[14px] font-[600]">
              {t('singleCampaign.description')}
            </label>
            {descriptions &&
              descriptions.map((description, index) => (
                <DescriptionPlatforms
                  key={description.id}
                  description={description}
                  updateDescription={(d) => updateDescription(index, d)}
                  deleteDescription={() => deleteDescription(index)}
                />
              ))}
            <div className="w-full flex items-center justify-center">
              <Button
                style="light-blue-underline"
                text={t('singleCampaign.addDescription')}
                type="button"
                customClassnames="hover:text-influentia-light-blue"
                id="addInfluencer"
                iconLeft={<CheckBoxReferralIcon />}
                onClick={() => addDescription()}
              />
            </div>
          </div>
          <div className="mt-5 flex flex-row gap-x-2">
            <Button
              style="light-blue"
              text={t('global.save')}
              loading={loading}
              type="submit"
              customClassnames="px-16"
            />
            <Button
              style="dark-blue"
              text={t('singleCampaign.migrateActionButton')}
              type="button"
              onClick={() => setShowMigratePopup(true)}
              customClassnames="px-16 font-normal"
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
};
